import { StateManager } from './core/StateManager';
import { PhysicsEngine } from './core/PhysicsEngine';
import { TripManager } from './core/TripManager';
import { PredictiveHealth } from './core/PredictiveHealth';
import { RequestQueue } from './modbus/RequestQueue';
import { Database as DB } from './store/Database';
import { Gauges, Oscilloscope, Terminal, StatusBits, Recommendations, NotificationToast, PredictivePanel, CatalogSelector, ProfileBadge } from './ui';
import { PollingService, SerialManager, TrendDashboard, CatalogService } from './services';
import { AuthModal, PricingModal, MultiSessionDashboard } from './ui';
import { fetchAllRemoteCatalogs } from './services/api/catalog';
import { isSupabaseConfigured } from './services/api';
import { NotificationService } from './services/predictive';
import { AuditLogger, type AuditEventType } from './services/security';
import { DRIVE_PROFILES } from './config/profiles';
import { SIMULATION } from './config/constants';
import {
  setupExportButtons,
  setupHealthBindings,
  setupKeyboardShortcuts,
  setupSimulationControls,
  setupTelemetrySampling,
  setupTripControls,
} from './ui/AppBindings';

function getElement<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Element #${id} not found`);
  return el as T;
}

async function main(): Promise<void> {
  // --- State ---
  const state = new StateManager();

  // --- Audit Logger ---
  const audit = new AuditLogger();
  audit.log('app.start', 'PREVDRIVE inicializado', 'info');

  // --- Persistência ---
  const db = new DB();
  try {
    const saved = await db.loadState();
    if (saved) {
      state.loadSnapshot(saved);
    }
  } catch {
    // IndexedDB pode não estar disponível
  }

  setInterval(async () => {
    try {
      await db.saveState(state.getSnapshot());
    } catch {
      // silent
    }
  }, SIMULATION.AUTO_SAVE_INTERVAL_MS);

  // --- Core ---
  const physics = new PhysicsEngine(state);
  const tripManager = new TripManager(state);
  const predictiveHealth = new PredictiveHealth(state);

  // --- Catalog Service ---
  const catalogService = new CatalogService();
  catalogService.registerDynamicProfiles();
  catalogService.initOnlineSync();

  // --- Auth ---
  const pricingModal = new PricingModal(document.body);
  document.getElementById('btnPricing')?.addEventListener('click', () => pricingModal.open());

  // --- Live Dashboard ---
  const liveDashboard = new MultiSessionDashboard(document.body);
  document.getElementById('btnLiveDashboard')?.addEventListener('click', () => liveDashboard.open());

  const authWall = document.getElementById('authWall');
  const appContainer = document.querySelector('.app-container') as HTMLElement;

  function toggleAppVisibility(show: boolean): void {
    if (authWall) authWall.classList.toggle('hidden', show);
    if (appContainer) appContainer.style.display = show ? 'flex' : 'none';
  }

  const authModal = new AuthModal(document.body, async (user) => {
    const userEmailEl = document.getElementById('userEmail');
    const btnAuth = document.getElementById('btnAuth');
    const userMenu = document.getElementById('userMenu');

    if (user) {
      toggleAppVisibility(true);
      if (btnAuth) btnAuth.classList.add('hidden');
      if (userMenu) userMenu.classList.remove('hidden');
      if (userEmailEl) userEmailEl.textContent = user.name || user.email;
      if (isSupabaseConfigured()) {
        try {
          const remote = await fetchAllRemoteCatalogs();
          let added = 0;
          for (const { key, catalog } of remote) {
            if (!catalogService.getCatalogs().find(c => c.key === key)) {
              const { added: modelsAdded } = catalogService.addCatalog(catalog, key);
              if (modelsAdded === 0) continue;
              const remoteId = key.replace('supabase-', '');
              catalogService.setRemoteId(key, remoteId);
              added++;
            }
          }
          if (added > 0) audit.log('catalog.sync', `Sincronizados ${added} catálogo(s) da nuvem`, 'info');
          catalogService.syncPending();
        } catch { /* silent */ }
      }
    } else {
      toggleAppVisibility(false);
      if (btnAuth) btnAuth.classList.remove('hidden');
      if (userMenu) userMenu.classList.add('hidden');
    }
  }, (type, detail) => audit.log(type as AuditEventType, detail, 'info'));

  // Start with app hidden if not logged in
  toggleAppVisibility(authModal.getUser() !== null);

  document.getElementById('btnLogout')?.addEventListener('click', () => authModal.logout());
  document.getElementById('btnAuth')?.addEventListener('click', () => authModal.open());
  document.getElementById('btnAuthWall')?.addEventListener('click', () => authModal.open());

  // --- DOM ---
  const dom = {
    btnConnectSerial: getElement<HTMLButtonElement>('btnConnectSerial'),
    ledMode: getElement<HTMLElement>('ledMode'),
    labelMode: getElement<HTMLElement>('labelMode'),
    ledSerial: getElement<HTMLElement>('ledSerial'),
    labelSerial: getElement<HTMLElement>('labelSerial'),

    simTargetFreq: getElement<HTMLInputElement>('simTargetFreq'),
    simTargetFreqVal: getElement<HTMLElement>('simTargetFreqVal'),
    simLoad: getElement<HTMLInputElement>('simLoad'),
    simLoadVal: getElement<HTMLElement>('simLoadVal'),
    simVoltageDrop: getElement<HTMLInputElement>('simVoltageDrop'),
    simVoltageDropVal: getElement<HTMLElement>('simVoltageDropVal'),
    simImbalance: getElement<HTMLInputElement>('simImbalance'),
    simImbalanceVal: getElement<HTMLElement>('simImbalanceVal'),
    simDaysOffline: getElement<HTMLInputElement>('simDaysOffline'),
    simDaysOfflineVal: getElement<HTMLElement>('simDaysOfflineVal'),
    simLooseTerminal: getElement<HTMLInputElement>('simLooseTerminal'),
    simCloggedHeatsink: getElement<HTMLInputElement>('simCloggedHeatsink'),

    tripBanner: getElement<HTMLElement>('tripBanner'),
    tripBannerText: getElement<HTMLElement>('tripBannerText'),
    tripBannerReset: getElement<HTMLElement>('tripBannerReset'),

    fanMainBar: getElement<HTMLElement>('fanMainBar'),
    fanMainHoursText: getElement<HTMLElement>('fanMainHours'),
    fanControlBar: getElement<HTMLElement>('fanControlBar'),
    fanControlHoursText: getElement<HTMLElement>('fanControlHours'),
    capReformingStatus: getElement<HTMLElement>('capReformingStatus'),

    clearTerminalBtn: getElement<HTMLButtonElement>('clearTerminalBtn'),
  };

  // --- UI Modules ---
  const terminal = new Terminal(getElement('terminalConsole'));
  new ProfileBadge(getElement('profileBadge'), state);
  new Gauges(state, {
    freq: getElement('gaugeFreq'),
    current: getElement('gaugeCurrent'),
    dcVoltage: getElement('gaugeDcVoltage'),
    temp: getElement('gaugeTemp'),
  });

  const oscilloscope = new Oscilloscope(
    getElement<HTMLCanvasElement>('oscilloscope'),
    state,
  );

  new StatusBits(state, {
    bitAlarm13: getElement('bitAlarm13'),
    bitAlarm7: getElement('bitAlarm7'),
    bitAlarm8: getElement('bitAlarm8'),
    bitAlarm29: getElement('bitAlarm29'),
    bitWarn4: getElement('bitWarn4'),
    bitWarn25: getElement('bitWarn25'),
  });

  new Recommendations(
    state,
    getElement('recList'),
  );

  // --- Predictive UI ---
  const predictivePanel = new PredictivePanel(state, {
    rulFanMain: getElement('rulFanMain'),
    rulFanControl: getElement('rulFanControl'),
    rulCapacitor: getElement('rulCapacitor'),
    rulIGBT: getElement('rulIGBT'),
    rulBearing: getElement('rulBearing'),
    anomaliesList: getElement('anomaliesList'),
    rulesList: getElement('rulesList'),
    healthScore: getElement('healthScore'),
    lastUpdated: getElement('lastUpdated'),
    partsBOMList: getElement('partsBOMList'),
  });
  predictivePanel.start(5000);

  // --- Analytics ---
  const trendDashboard = new TrendDashboard({
    containerFreq: document.getElementById('trend-freq'),
    containerCurrent: document.getElementById('trend-current'),
    containerTemp: document.getElementById('trend-temp'),
    containerDC: document.getElementById('trend-dc'),
    state,
  });
  trendDashboard.start(5000);

  // --- Notification System ---
  const notificationService = new NotificationService();
  new NotificationToast(notificationService);

  const serialManager = new SerialManager(state, terminal);
  const requestQueue = new RequestQueue({
    maxConcurrent: 1,
    defaultTimeout: 2000,
    defaultRetryCount: 1,
    defaultRetryDelay: 300,
  });
  const polling = new PollingService(state, terminal, serialManager, requestQueue);

  // --- Catalog Selector ---
  const catalogContainer = document.getElementById('driveProfileSelectContainer');
  if (catalogContainer) {
    new CatalogSelector(catalogContainer, catalogService, {
      onProfileChange: (key: string) => {
        state.setProfile(key);
        const profile = DRIVE_PROFILES[key];
        if (profile) {
          terminal.log(`[PERFIL] Alterado para: ${profile.name}`, 'system');
          state.set('dcVoltage', profile.nominalDC);
          state.set('heatsinkTemp', 30.0);
          state.set('currentAmp', 0.0);
          audit.log('profile.change', `Perfil alterado para ${profile.name}`, 'info');
        }
      },
    });
  }

  // --- Bindings ---
  const healthBindings = setupHealthBindings(dom, state, predictiveHealth);
  setupSimulationControls(dom, state, terminal, () => healthBindings.updateCapacitorHealth());
  setupTripControls(dom, tripManager, terminal, audit);
  setupExportButtons(db, state, terminal, audit);
  setupKeyboardShortcuts(dom, tripManager, terminal);
  setupTelemetrySampling(db, state, trendDashboard);

  // Serial
  dom.btnConnectSerial.addEventListener('click', async () => {
    if (serialManager.isConnected) {
      await serialManager.disconnect();
      dom.ledMode.className = 'led active-sim';
      dom.labelMode.innerText = 'Modo Simulação';
      dom.ledSerial.className = 'led inactive';
      dom.labelSerial.innerText = 'RS-485 Desconectado';
      dom.btnConnectSerial.innerText = 'Conectar Serial (RS-485)';
      polling.start();
      audit.log('serial.disconnect', 'Conexão RS-485 encerrada', 'info');
    } else {
      try {
        polling.stop();
        await serialManager.connect();
        dom.ledMode.className = 'led inactive';
        dom.labelMode.innerText = 'Simulação Pausada';
        dom.ledSerial.className = 'led active-serial';
        dom.labelSerial.innerText = 'RS-485 Conectado';
        dom.btnConnectSerial.innerText = 'Desconectar Serial';
        audit.log('serial.connect', 'Conexão RS-485 estabelecida', 'info');
      } catch {
        polling.start();
      }
    }
  });

  // Terminal clear
  dom.clearTerminalBtn.addEventListener('click', () => terminal.clear());

  // --- Notificação de trip via toast ---
  state.subscribe('activeTrip', (trip) => {
    if (trip) {
      notificationService.notify({
        ruleId: 'trip-detected',
        ruleName: 'Trip Detectado',
        triggered: true,
        severity: 'critical',
        message: `Inversor desarmado: ${trip}`,
        evaluatedAt: new Date(),
      });
    }
  });

  // --- Start Loops ---
  setInterval(() => physics.update(), SIMULATION.PHYSICS_INTERVAL_MS);
  polling.start();
  oscilloscope.start();
  healthBindings.updateFanHealth();
  healthBindings.updateCapacitorHealth();
}

document.addEventListener('DOMContentLoaded', main);